import Navbar from "./Navbar";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { useGSAP } from "@gsap/react";
import { useRef, useState, useEffect, useCallback } from "react";
import Footer from "./Footer";
import SplitText from "./SplitText";
import useEmblaCarousel from "embla-carousel-react";

gsap.registerPlugin(useGSAP, ScrollTrigger, ScrollSmoother);

const PROJECTS = [
  {
    title: "PullRates",
    image: "pr.png",
    url: "https://pullrates.com",
    links: [{ label: "What's New", href: "https://pullrates.com/whats-new" }],
  },
  {
    title: "Chaptr",
    image: "cc.png",
    url: "https://www.chaptr.net",
    links: [
      { label: "Source Code", href: "https://github.com/cduarte3/chapterchat" },
    ],
  },
  {
    title: "F2Read",
    image: "f2.png",
    url: "https://www.npmjs.com/package/f2read?activeTab=readme",
    links: [
      { label: "Source Code", href: "https://github.com/cduarte3/f2read" },
    ],
  },
  {
    title: "BlackJack Pygame",
    image: "bjack.png",
    url: "https://christianduarte7.itch.io/blackjack-demo",
    links: [
      { label: "Source Code", href: "https://github.com/cduarte3/bjackv2" },
    ],
  },
  {
    title: "Connect 4 Pygame",
    image: "con4.png",
    url: "https://christianduarte7.itch.io/connect-four",
    links: [
      { label: "Source Code", href: "https://github.com/cduarte3/connect4" },
    ],
  },
  {
    title: "AWS Microservice",
    image: "aws.jpg",
    url: "https://www.youtube.com/watch?v=D5WmuvlWnvE",
    links: [],
  },
  {
    title: "Weather App",
    image: "weather.jpg",
    url: "https://weather-app-gamma-cyan-66.vercel.app",
    links: [
      { label: "Source Code", href: "https://github.com/cduarte3/weather-app" },
    ],
  },
];

const ARROW_BUTTON =
  "w-11 h-11 flex items-center justify-center rounded-full bg-[#EE6164] hover:bg-[#d45558] text-white transition-colors disabled:opacity-30 disabled:hover:bg-[#EE6164]";

export default function Projects() {
  const main = useRef();
  const smoother = useRef();
  const [isIOS, setIsIOS] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    slidesToScroll: "auto",
  });
  const [selectedSnap, setSelectedSnap] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // Keep the dots and arrow states in sync with the carousel position
  useEffect(() => {
    if (!emblaApi) return;
    const update = () => {
      setSelectedSnap(emblaApi.selectedScrollSnap());
      setSnapCount(emblaApi.scrollSnapList().length);
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    update();
    emblaApi.on("select", update).on("reInit", update);
    return () => {
      emblaApi.off("select", update).off("reInit", update);
    };
  }, [emblaApi]);

  useEffect(() => {
    const detectIOS = () => {
      return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
      );
    };

    setIsIOS(detectIOS());
  }, []);

  useGSAP(
    () => {
      smoother.current = ScrollSmoother.create({
        smooth: 2,
        effects: true,
      });
      smoother.current.scrollTo(0, false);

      ScrollTrigger.create({
        trigger: ".box-c",
        pin: true,
        start: "center center",
        end: "+=300",
        markers: false,
      });
    },
    { scope: main },
  );

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <>
      <div id="smooth-wrapper" ref={main}>
        <div id="smooth-content">
          <Navbar currentPage="projects" />
          <div className="bg-[#1C1C1C] mb-[-5px] pb-12 sm:pb-20">
            <div className="mt-[-5px] relative flex flex-col items-center">
              {isIOS ? (
                <h1 className="mt-28 font-libre font-black text-white text-[15vw] md:text-[10vw] text-center leading-[0.9] tracking-[-0.06em] px-4 mb-16 md:mb-28">
                  PROJECTS
                </h1>
              ) : (
                <SplitText
                  text="PROJECTS"
                  className="mt-28 font-libre font-black text-white text-[15vw] md:text-[10vw] text-center leading-[0.9] tracking-[-0.06em] px-4 mb-16 md:mb-28"
                />
              )}
              <div className="w-full overflow-hidden" ref={emblaRef}>
                <div className="flex items-start gap-6 sm:gap-10 px-[11vw] sm:px-10 lg:px-20">
                  {PROJECTS.map((project) => (
                    <div
                      key={project.title}
                      className="flex-[0_0_auto] w-[78vw] sm:w-[320px] lg:w-[380px] flex flex-col items-center"
                    >
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open ${project.title}`}
                        className="block w-full aspect-[4/5] border-4 border-[#EE6164] rounded-[3rem] lg:rounded-[4rem] bg-cover bg-center bg-no-repeat"
                        style={{ backgroundImage: `url(${project.image})` }}
                      ></a>
                      <h2 className="font-libre font-black text-white text-2xl lg:text-3xl mt-5 text-center">
                        {project.title}
                      </h2>
                      {project.links.map((link) => (
                        <a
                          key={link.href}
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-radley text-[#EE6164] text-xl lg:text-2xl mt-1 underline hover:italic"
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              {snapCount > 1 && (
                <div className="flex items-center justify-center gap-5 mt-10">
                  <button
                    className={ARROW_BUTTON}
                    onClick={scrollPrev}
                    disabled={!canPrev}
                    aria-label="Previous projects"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 532 532">
                      <path
                        fill="currentColor"
                        d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
                      ></path>
                    </svg>
                  </button>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: snapCount }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => emblaApi && emblaApi.scrollTo(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        className={`h-2.5 rounded-full transition-all duration-300 ${
                          i === selectedSnap
                            ? "w-6 bg-[#EE6164]"
                            : "w-2.5 bg-[#4a4a4a] hover:bg-[#6a6a6a]"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    className={ARROW_BUTTON}
                    onClick={scrollNext}
                    disabled={!canNext}
                    aria-label="Next projects"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 532 532">
                      <path
                        fill="currentColor"
                        d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
                      ></path>
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
